import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  Typography,
  Box,
  Divider,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from '@apollo/client/react';
import {
  MoneyTransactionFragmentFragment,
  Invoice,
  CreateInvoicePaymentDocument,
  CreateInvoicePaymentMutation,
  CreateInvoicePaymentMutationVariables,
} from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../../components/app-context';
import { InvoiceSelectionDialog } from './invoice-selection-dialog';

interface PaymentAssignmentDialogProps {
  open: boolean;
  transaction: MoneyTransactionFragmentFragment | null;
  onClose: (doRefresh: boolean) => void;
  onRefresh: () => void;
}

export function PaymentAssignmentDialog({ open, transaction, onClose, onRefresh }: PaymentAssignmentDialogProps) {
  const { show } = useAlert();
  const app = useApp();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [amount, setAmount] = useState('');
  const [invoiceSelectionOpen, setInvoiceSelectionOpen] = useState(false);

  const [createAssignment, { loading: creating }] = useMutation<
    CreateInvoicePaymentMutation,
    CreateInvoicePaymentMutationVariables
  >(CreateInvoicePaymentDocument);

  // Calculate available unreconciled amount
  const availableAmount = transaction ? transaction.value - transaction.reconciledValue : 0;

  useEffect(() => {
    if (transaction) {
      // Set default amount to the full unreconciled value
      setAmount(availableAmount.toFixed(2));
      setSelectedInvoice(null);
    }
  }, [transaction, availableAmount]);

  const handleInvoiceSelect = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceSelectionOpen(false);

    // Adjust amount if needed to not exceed the remaining invoice amount
    const remaining = invoice.totalAmount - (invoice.paidAmount || 0);
    const maxAmount = Math.min(availableAmount, remaining);
    setAmount(maxAmount.toFixed(2));
  };

  const handleSave = async () => {
    if (!transaction || !selectedInvoice) {
      show.error('Vyberte faktúru.');
      return;
    }

    const amountValue = parseFloat(amount);
    if (!amountValue || amountValue <= 0) {
      show.error('Zadajte platnú sumu.');
      return;
    }

    if (amountValue > availableAmount) {
      show.error(
        'Suma nemôže presiahnuť nespárovanú hodnotu transakcie.',
        `${app.formatNumber(amountValue, 2)} > ${app.formatNumber(availableAmount, 2)}`,
      );
      return;
    }

    const remaining = selectedInvoice.totalAmount - (selectedInvoice.paidAmount || 0);
    if (amountValue > remaining) {
      show.error(
        'Suma nemôže presiahnuť zostávajúcu hodnotu faktúry.',
        `${app.formatNumber(amountValue, 2)} > ${app.formatNumber(remaining, 2)}`,
      );
      return;
    }

    try {
      const result = await createAssignment({
        variables: {
          input: {
            transactionId: transaction.id,
            invoiceId: selectedInvoice.id,
            amount: amountValue,
          },
        },
      });

      if (result.data?.createInvoicePayment?.errors?.length) {
        const error = result.data.createInvoicePayment.errors[0];
        show.error('Chyba pri vytváraní priradenia.', error.code);
        return;
      }

      show.info('Platba bola úspešne priradená k faktúre.');
      onClose(true);
    } catch (error) {
      show.error('Nepodarilo sa priradiť platbu.', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const handleClose = () => {
    setSelectedInvoice(null);
    setAmount('');
    onClose(false);
  };

  if (!transaction) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Priradenie platby
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Transakcia
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Dátum:</Typography>
                  <Typography variant="body2">{app.formatDate(transaction.transactionDate)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Suma:</Typography>
                  <Typography variant="body2">{app.formatNumber(transaction.value, 2)} €</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Už spárované:</Typography>
                  <Typography variant="body2">{app.formatNumber(transaction.reconciledValue, 2)} €</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight="bold">
                    K dispozícii:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {app.formatNumber(availableAmount, 2)} €
                  </Typography>
                </Stack>
                {transaction.reference && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Referencia:</Typography>
                    <Typography variant="body2">{transaction.reference}</Typography>
                  </Stack>
                )}
                {transaction.note && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ wordWrap: 'break-word' }}>
                      Poznámka: {transaction.note}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            <Divider />

            <Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Vybraná faktúra:</Typography>
                <Button variant="outlined" size="small" onClick={() => setInvoiceSelectionOpen(true)}>
                  {selectedInvoice ? 'Zmeniť faktúru' : 'Vybrať faktúru'}
                </Button>
              </Stack>

              {selectedInvoice ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Číslo faktúry:</Typography>
                      <Typography variant="body2">{selectedInvoice.invoiceNo}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Referencia:</Typography>
                      <Typography variant="body2">{selectedInvoice.refNo}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Celková suma:</Typography>
                      <Typography variant="body2">{app.formatNumber(selectedInvoice.totalAmount, 2)} €</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Už zaplatené:</Typography>
                      <Typography variant="body2">{app.formatNumber(selectedInvoice.paidAmount || 0, 2)} €</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" fontWeight="bold">
                        Zostáva zaplatiť:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {app.formatNumber(selectedInvoice.totalAmount - (selectedInvoice.paidAmount || 0), 2)} €
                      </Typography>
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {selectedInvoice.description}
                    </Typography>
                    {selectedInvoice.contract && (
                      <Typography variant="body2" color="text.secondary">
                        Zmluva: {selectedInvoice.contract.title}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nie je vybraná žiadna faktúra
                </Typography>
              )}
            </Box>

            <TextField
              label="Suma na priradenie"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              required
              inputProps={{ step: '0.01', min: '0', max: availableAmount.toString() }}
              helperText={`Maximálna suma: ${app.formatNumber(availableAmount, 2)} €`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Zrušiť</Button>
          <Button onClick={handleSave} variant="contained" disabled={creating || !selectedInvoice}>
            {creating ? 'Ukladám...' : 'Priradiť'}
          </Button>
        </DialogActions>
      </Dialog>

      <InvoiceSelectionDialog
        open={invoiceSelectionOpen}
        onClose={() => setInvoiceSelectionOpen(false)}
        onSelect={handleInvoiceSelect}
      />
    </>
  );
}
